import { Box, Badge, TextField, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLink,
  BackButton,
  CloseButtonIcon,
  DatePickerButton,
  Header,
  Icon,
  IconName,
  MobilePopoverCard,
  MobileSearchFieldButton,
} from "halifax";
import React, { useContext, useState, useEffect, useCallback, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import H from "history";
import {
  ActivitiesEntryTypeEnum,
  CombinedSearchResultType,
  CombinedSearchGroupTrackingType,
} from "redmond";
import {
  getExperiencesAvailabilityQuery,
  getExperiencesShopQuery,
  useIsTravelApp,
} from "@capone/common";
import dayjs from "dayjs";
import { getSelectionId, getSelectionName } from "../../../../utils/selectionHelpers";

import { PATH_AVAILABILITY, PATH_HOME, PATH_SHOP } from "../../../../utils/paths";

import { ClientContext } from "../../../../App";
import { MobileAvailabilitySearchControlConnectorProps } from "./container";
import { TravelWalletDrawer } from "../../../travel-wallet/components";
import "./styles.scss";
import { LocationSearch, MobileCalendarPicker } from "../../../common";
import * as textConstants from "../../../common/textConstants";
import { CombinedSearchDropdown } from "../../../search/components/CombinedSearchDropdown/component";
import {
  getMergedCombinedGroups,
  getCombinedAutocompleteLoading,
  getHasCombinedAutocompleteError,
  getEntryTypesAll,
} from "../../../search/reducer/selectors";
import { getSelectedGroupedExperience } from "../../reducer/selectors";
import {
  fetchCombinedAutocomplete,
  clearCombinedAutocomplete,
  setEntryType,
} from "../../../search/actions/actions";
import {
  COMBINED_GROUP_TO_ENTRY_TYPE,
  CombinedSearchGroupType,
  CombinedSearchSuggestion,
} from "../../../search/types";
import {
  COMBINED_SEARCH_EXPERIMENT,
  COMBINED_SEARCH_IMAGES_EXPERIMENT,
  COMBINED_SEARCH_IMAGES_VARIANTS,
  DATELESS_SEARCH_EXPERIMENT,
  AVAILABLE as EXPERIMENT_AVAILABLE,
  getExperimentVariant as getExpVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../../context/experiments";
import { IStoreState } from "../../../../reducers/types";
import { useCombinedSearchAnalytics } from "../../../../hooks/useCombinedSearchAnalytics";

export interface IMobileAvailabilitySearchControlOwnProps {
  calendarOpen?: boolean;
  onCalendarOpenChange?: (open: boolean) => void;
}

export interface IMobileAvailabilitySearchControlProps
  extends RouteComponentProps,
    MobileAvailabilitySearchControlConnectorProps,
    IMobileAvailabilitySearchControlOwnProps {}

export const MobileAvailabilitySearchControl = (
  props: IMobileAvailabilitySearchControlProps
) => {
  const {
    history,
    walletItemCount,
    fromDate,
    untilDate,
    searchLocation,
    locationCategories,
    loading,
    hasLocationAutocompleteError,
    setFromDate,
    setUntilDate,
    setLocation,
    fetchInitialExperiencesAvailability,
    fetchLocationCategories,
    setLocationAutocompleteError,
    resetFilterAndSort,
    calendarOpen: calendarOpenProp,
    onCalendarOpenChange,
  } = props;
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [calendarModalOpenLocal, setCalendarModalOpenLocal] = useState(false);

  const calendarModalOpen = calendarOpenProp ?? calendarModalOpenLocal;
  const setCalendarModalOpen = onCalendarOpenChange ?? setCalendarModalOpenLocal;
  const [travelWalletDrawerOpen, setTravelWalletDrawerOpen] = useState(false);

  const { showCoreHomepage } = useContext(ClientContext);

  const [curLocation, setCurLocation] = useState(searchLocation);
  const [curFromDate, setCurFromDate] = useState(fromDate);
  const [curUntilDate, setCurUntilDate] = useState(untilDate);

  const { isTravelApp } = useIsTravelApp();
  const dispatch = useDispatch();
  const expState = useExperiments();

  const isCombinedSearchEnabled =
    getExpVariant(expState.experiments, COMBINED_SEARCH_EXPERIMENT) ===
    EXPERIMENT_AVAILABLE;

  const isDatelessSearchEnabled =
    getExpVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    EXPERIMENT_AVAILABLE;

  const combinedImageVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    COMBINED_SEARCH_IMAGES_EXPERIMENT,
    COMBINED_SEARCH_IMAGES_VARIANTS,
  );

  // Combined search state (only used when experiment enabled)
  const combinedGroups = useSelector((state: IStoreState) =>
    getMergedCombinedGroups(state),
  );
  const combinedLoading = useSelector(getCombinedAutocompleteLoading);
  const hasCombinedError = useSelector(getHasCombinedAutocompleteError);
  const selectedGroupedExperience = useSelector(getSelectedGroupedExperience);
  const entryTypesAll = useSelector(getEntryTypesAll);

  // Build display label: "Category - Destination" when filtered, else just destination
  const searchDisplayLabel = (() => {
    const tagName = selectedGroupedExperience?.tag?.name;
    const destName = searchLocation?.label;
    if (tagName && destName) return `${tagName} - ${destName}`;
    return tagName || destName || "";
  })();

  const [combinedQuery, setCombinedQuery] = useState(
    searchLocation?.label || ""
  );
  const [combinedHasSearched, setCombinedHasSearched] = useState(false);
  const combinedInputRef = useRef<HTMLInputElement>(null);

  const {
    resetRefs: resetAnalyticsRefs,
    trackFocused,
    trackTyped,
    trackResultClicked,
    trackCleared,
    trackNoResults,
  } = useCombinedSearchAnalytics({
    pageContext: "availability",
    hasRecentSearches: false,
  });

  // Pre-fill and select all text when location modal opens
  useEffect(() => {
    if (locationModalOpen && isCombinedSearchEnabled) {
      setCombinedQuery(searchLocation?.label || "");
      setCombinedHasSearched(false);
      trackFocused();
      setTimeout(() => {
        combinedInputRef.current?.focus();
        combinedInputRef.current?.select();
      }, 100);
    } else if (!locationModalOpen) {
      resetAnalyticsRefs();
    }
  }, [locationModalOpen, isCombinedSearchEnabled, searchLocation?.label, trackFocused, resetAnalyticsRefs]);

  // Fire typed/no_results events when the API response arrives,
  // but only while the location modal is open
  const prevCombinedLoadingRef = useRef(combinedLoading);
  useEffect(() => {
    if (
      locationModalOpen &&
      prevCombinedLoadingRef.current &&
      !combinedLoading &&
      combinedQuery.trim().length > 0
    ) {
      trackTyped(combinedQuery);
      if (
        !hasCombinedError &&
        combinedGroups.every((g) => g.suggestions.length === 0)
      ) {
        trackNoResults(combinedQuery);
      }
    }
    prevCombinedLoadingRef.current = combinedLoading;
  }, [locationModalOpen, combinedLoading, combinedGroups, hasCombinedError, combinedQuery, trackTyped, trackNoResults]);

  const handleCombinedInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = e.target;
      setCombinedQuery(value);
      setCombinedHasSearched(true);
      if (value.trim().length > 0) {
        const destId = searchLocation?.id
          ? getSelectionId(searchLocation.id)
          : undefined;
        dispatch(
          fetchCombinedAutocomplete({
            query: value,
            destinationId: destId,
            locationLabel: !destId ? searchLocation?.label : undefined,
          }),
        );
      } else {
        dispatch(clearCombinedAutocomplete());
      }
    },
    [dispatch, searchLocation],
  );

  const handleCombinedClear = useCallback(() => {
    setCombinedQuery("");
    dispatch(clearCombinedAutocomplete());
    trackCleared();
    combinedInputRef.current?.focus();
  }, [dispatch, trackCleared]);

  const handleCombinedResultClick = useCallback(
    (
      suggestion: CombinedSearchSuggestion,
      groupType: CombinedSearchGroupType,
      index: number,
    ) => {
      const resultTypeMap: Record<CombinedSearchGroupType, CombinedSearchResultType> = {
        Destination: CombinedSearchResultType.Destination,
        Category: CombinedSearchResultType.Category,
        Attraction: CombinedSearchResultType.Attraction,
        Product: CombinedSearchResultType.Product,
        Collection: CombinedSearchResultType.Collection,
      };
      const groupTrackingMap: Record<CombinedSearchGroupType, CombinedSearchGroupTrackingType> = {
        Destination: "Destination",
        Category: "Categories",
        Attraction: "Attraction",
        Product: "Products",
        Collection: "Collection",
      };
      trackResultClicked({
        resultType: resultTypeMap[groupType],
        resultIndex: index,
        groupType: groupTrackingMap[groupType],
        searchQuery: combinedQuery,
        destinationId: suggestion.destinationId,
        destinationName: suggestion.destinationName,
      });

      // Store enrichment for tracking
      try {
        localStorage.setItem(
          "combined_search_enrichment",
          JSON.stringify({
            label: suggestion.label,
            thumbnailUrl: suggestion.thumbnailUrl,
            activityCount: suggestion.activityCount,
            suggestionType: suggestion.suggestionType || groupType,
          }),
        );
      } catch {
        // Ignore
      }

      const entryTypeForResult = COMBINED_GROUP_TO_ENTRY_TYPE[groupType];
      if (entryTypeForResult != null) {
        dispatch(setEntryType(entryTypeForResult));
      }

      setLocationModalOpen(false);

      switch (groupType) {
        case "Destination":
          history.push(
            `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
              suggestion.label,
              curFromDate,
              curUntilDate,
              undefined,
              isTravelApp,
              ActivitiesEntryTypeEnum.COMBINED_SEARCH_DESTINATION,
              undefined,
              undefined,
              entryTypesAll ?? [],
            )}`,
          );
          resetFilterAndSort();
          setFromDate(curFromDate);
          setUntilDate(curUntilDate);
          fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
          break;
        case "Category":
        case "Attraction": {
          const destName =
            searchLocation?.label || suggestion.destinationName || suggestion.label;
          const entryType =
            groupType === "Category"
              ? ActivitiesEntryTypeEnum.COMBINED_SEARCH_CATEGORY
              : ActivitiesEntryTypeEnum.COMBINED_SEARCH_ATTRACTION;
          const kindSuffix = groupType === "Category" ? "_0" : "_1";
          const filterParam = `&categoryTagId=${suggestion.id}${kindSuffix}`;
          history.push(
            `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
              destName,
              curFromDate,
              curUntilDate,
              undefined,
              isTravelApp,
              entryType,
              undefined,
              undefined,
              entryTypesAll ?? [],
            )}${filterParam}`,
          );
          resetFilterAndSort();
          setFromDate(curFromDate);
          setUntilDate(curUntilDate);
          fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
          break;
        }
        case "Product": {
          const shopFromDate = curFromDate || dayjs().toDate();
          const shopUntilDate = curUntilDate || dayjs().add(7, "day").toDate();
          // Store shopContext with current filter so back navigation preserves it
          try {
            const selGroup = selectedGroupedExperience;
            const destId = searchLocation?.id
              ? getSelectionId(searchLocation.id)
              : "";
            localStorage.setItem(
              `shop-context-${suggestion.id}`,
              JSON.stringify({
                destinationId: destId,
                entryTag: selGroup?.tag,
                searchLocation: searchLocation?.label,
              }),
            );
          } catch {
            // Ignore
          }
          history.push(
            `${PATH_SHOP}${getExperiencesShopQuery(
              { value: suggestion.id },
              shopFromDate,
              shopUntilDate,
              suggestion.destinationName,
              isTravelApp,
              undefined,
              ActivitiesEntryTypeEnum.COMBINED_SEARCH_PRODUCT,
              entryTypesAll ?? [],
            )}`,
          );
          break;
        }
        default:
          break;
      }
    },
    [
      history,
      curFromDate,
      curUntilDate,
      searchLocation,
      isTravelApp,
      resetFilterAndSort,
      setFromDate,
      setUntilDate,
      fetchInitialExperiencesAvailability,
      trackResultClicked,
      combinedQuery,
      entryTypesAll,
    ],
  );

  useEffect(() => {
    setCurLocation(searchLocation);
  }, [searchLocation]);

  useEffect(() => {
    setCurFromDate(fromDate);
  }, [fromDate]);

  useEffect(() => {
    setCurUntilDate(untilDate);
  }, [untilDate]);

  // eslint-disable-next-line no-shadow -- history param matches parent scope intentionally
  const handleSearch = (history: H.History) => {
    // Preserve existing filter params (e.g., categoryTagId) when updating dates/location
    const existingParams = new URLSearchParams(history.location.search);
    const categoryTagId = existingParams.get("categoryTagId");
    const baseQuery = `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
      curLocation?.id ? getSelectionName(curLocation.id) : "",
      curFromDate,
      curUntilDate || curFromDate,
      undefined,
      isTravelApp
    )}`;
    const filterParam = categoryTagId ? `&categoryTagId=${categoryTagId}` : "";
    history.push(`${baseQuery}${filterParam}`);

    resetFilterAndSort();
    setFromDate(curFromDate);
    setUntilDate(curUntilDate);
    setLocation(curLocation);
    fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);

    if (calendarModalOpen) setCalendarModalOpen(false);
    if (locationModalOpen) setLocationModalOpen(false);
  };

  return (
    <>
      <Header
        className={clsx("mobile-availability-search-header", {
          "core-homepage": showCoreHomepage,
        })}
        left={
          !isTravelApp ? (
            <BackButton
              className="mobile-availability-go-back"
              onClick={() => history.push(PATH_HOME)}
            />
          ) : undefined
        }
        center={
          <Box className="mobile-availability-search-and-date">
            <MobileSearchFieldButton
              value={isCombinedSearchEnabled ? searchDisplayLabel : searchLocation?.label}
              onClick={() => setLocationModalOpen(true)}
            />
            <DatePickerButton
              startDate={curFromDate}
              endDate={curUntilDate}
              renderCalendarPopup={() => (
                <MobilePopoverCard
                  open={calendarModalOpen}
                  className="mobile-search-calendar-popup"
                  onClose={() => setCalendarModalOpen(false)}
                  headerElement={
                    <Box className="modal-header-container">
                      <Typography className="header-title">
                        {textConstants.UPDATE_CALENDAR_TEXT}
                      </Typography>
                      <ActionLink
                        className="modal-close-button"
                        onClick={() => setCalendarModalOpen(false)}
                        content={
                          <CloseButtonIcon className="close-button-icon" />
                        }
                        label="Close"
                        showTappableArea
                      />
                    </Box>
                  }
                  fullScreen
                >
                  <MobileCalendarPicker
                    fromDate={curFromDate}
                    untilDate={curUntilDate}
                    setFromDate={setCurFromDate}
                    setUntilDate={setCurUntilDate}
                    handleSearch={handleSearch}
                  />
                </MobilePopoverCard>
              )}
              onClick={() => setCalendarModalOpen(true)}
              dateFormat="MMM D"
              variant="minimal"
            />
          </Box>
        }
        right={
          !showCoreHomepage || isTravelApp ? (
            <ActionLink
              className="mobile-availability-wallet-entry"
              onClick={() => setTravelWalletDrawerOpen(true)}
              content={
                <Badge
                  className="wallet-count"
                  badgeContent={walletItemCount ? "" : undefined}
                >
                  <Icon name={IconName.TravelWalletIcon} />
                </Badge>
              }
            />
          ) : undefined
        }
      />
      <MobilePopoverCard
        open={locationModalOpen}
        className="mobile-search-location-popup"
        onClose={() => setLocationModalOpen(false)}
        headerElement={
          <Box className="modal-header-container">
            <Typography className="header-title">
              {textConstants.WHERE_TO}
            </Typography>
            <ActionLink
              className="modal-close-button"
              onClick={() => setLocationModalOpen(false)}
              content={<CloseButtonIcon className="close-button-icon" />}
              label="Close"
              showTappableArea
            />
          </Box>
        }
        fullScreen
      >
        {isCombinedSearchEnabled ? (
          <Box className="mobile-combined-search-in-modal">
            <Box className="mobile-combined-search-input-wrapper">
              <Icon name={IconName.B2BMapPin} ariaLabel="" aria-hidden />
              <TextField
                inputRef={combinedInputRef}
                className="mobile-combined-search-input"
                placeholder={
                  searchLocation?.label
                    ? `Search in ${searchLocation.label}`
                    : "Search by name or keyword"
                }
                value={combinedQuery}
                onChange={handleCombinedInputChange}
                fullWidth
                InputProps={{ disableUnderline: true }}
              />
              {combinedQuery.length > 0 && (
                <button
                  className="mobile-combined-search-clear"
                  onClick={handleCombinedClear}
                  aria-label="Clear search"
                  type="button"
                >
                  <CloseButtonIcon className="clear-icon" />
                </button>
              )}
            </Box>
            <Box className="mobile-combined-search-results">
              <CombinedSearchDropdown
                groups={combinedGroups}
                loading={combinedLoading}
                hasError={hasCombinedError}
                query={combinedQuery}
                recentSearches={[]}
                tripDestinations={[]}
                showTrips={false}
                tripsLoading={false}
                imageVariant={combinedImageVariant}
                hasSearched={combinedHasSearched}
                onResultClick={handleCombinedResultClick}
                onRecentSearchClick={() => {}}
                onTripClick={() => {}}
              />
            </Box>
          </Box>
        ) : (
          <LocationSearch
            locationSearchPrefixString={textConstants.WHERE_TO}
            onComplete={() => handleSearch(history)}
            onClickAutocompleteError={() => {
              history.push(PATH_HOME);
              if (hasLocationAutocompleteError) setLocationAutocompleteError(false);
            }}
            location={searchLocation}
            locationCategories={locationCategories}
            loading={loading}
            hasLocationAutocompleteError={hasLocationAutocompleteError}
            setLocation={setCurLocation}
            fetchLocationCategories={fetchLocationCategories}
            setLocationAutocompleteError={setLocationAutocompleteError}
            continueOnSelect={false}
          />
        )}
      </MobilePopoverCard>
      <TravelWalletDrawer
        drawerOpen={travelWalletDrawerOpen}
        setDrawerOpen={setTravelWalletDrawerOpen}
      />
    </>
  );
};
